import config from 'src/shared/config';

export interface ComposerPlatformProduct {
    code: string;
    name: string;
    thumbnail?: string | null;
    metadata: Record<string, unknown>;
}

export interface ComposerPlatformVariant {
    code: string;
    sku?: string | null;
    name?: string | null;
    price: number;
    metadata: Record<string, unknown>;
}

export interface ComposerPlatformVariantCode {
    product: {
        code: string;
        metadata: Record<string, unknown>;
    };
    variant: ComposerPlatformVariant;
    zakekePlatform: true;
}

export const normalizeComposerPlatformMetadata = (value: unknown): Record<string, unknown> => {
    if (!value) return {};
    if (Array.isArray(value)) {
        return Object.fromEntries(value.flatMap((entry: any) => (entry?.key != null ? [[String(entry.key), entry.value]] : [])));
    }
    return typeof value === 'object' ? { ...(value as Record<string, unknown>) } : {};
};

export const parseComposerPlatformVariantCode = (value: string | undefined | null): ComposerPlatformVariantCode | null => {
    if (!value?.trim().startsWith('{')) return null;
    try {
        const parsed = JSON.parse(value);
        if (!parsed?.product?.code || !parsed?.variant?.code || parsed.zakekePlatform !== true) return null;
        return {
            product: {
                code: String(parsed.product.code),
                metadata: normalizeComposerPlatformMetadata(parsed.product.metadata)
            },
            variant: {
                code: String(parsed.variant.code),
                sku: parsed.variant.sku == null ? null : String(parsed.variant.sku),
                name: parsed.variant.name == null ? null : String(parsed.variant.name),
                price: Number(parsed.variant.price) || 0,
                metadata: normalizeComposerPlatformMetadata(parsed.variant.metadata)
            },
            zakekePlatform: true
        };
    } catch {
        return null;
    }
};

export const createComposerPlatformVariantCode = (product: ComposerPlatformProduct, variant: ComposerPlatformVariant) =>
    JSON.stringify({
        product: { code: product.code, metadata: normalizeComposerPlatformMetadata(product.metadata) },
        variant: {
            code: variant.code,
            sku: variant.sku ?? null,
            name: variant.name ?? null,
            price: variant.price || 0,
            metadata: normalizeComposerPlatformMetadata(variant.metadata)
        },
        zakekePlatform: true
    });

export const loadComposerPlatformVariants = async (productCode: string): Promise<ComposerPlatformVariant[]> => {
    const apiUrl = String(config.apiUrl || '').replace(/\/?$/, '/');
    const response = await fetch(`${apiUrl}MerchantProducts/${encodeURIComponent(productCode)}/variants`, {
        headers: {
            Authorization: `Bearer ${config.token}`,
            Accept: 'application/json',
            'Accept-Language': config.culture
        }
    });
    if (!response.ok) throw new Error(`Unable to load store variants (${response.status}).`);

    const payload = await response.json();
    if (!Array.isArray(payload)) throw new Error('The store variants response is invalid.');
    return payload.flatMap((item: any) => {
        const code = item?.code ?? item?.Code;
        if (code == null || String(code).trim() === '') return [];
        return [
            {
                code: String(code),
                sku: item?.sku ?? item?.Sku ?? null,
                name: item?.name ?? item?.Name ?? null,
                price: Number(item?.price ?? item?.Price) || 0,
                metadata: normalizeComposerPlatformMetadata(item?.metadata ?? item?.Metadata)
            }
        ];
    });
};
